/**
 * Bio prompts — curated icebreaker questions a user can optionally
 * answer to add personality to their profile.
 *
 * Pattern lifted from Hinge: ~30 prompts, user picks any 3-5, each
 * answer ≤ 120 chars. Renders as Q + A pairs in the Bio & Prompts
 * tile on the profile view.
 *
 * Schema-side, answers live on `profiles.prompt_answers` as a jsonb
 * array of `{ promptKey, answer }` objects. The schema enforces the
 * promptKey is a valid registered prompt, and the answer length cap.
 *
 * Adding a new prompt: append to the list. Removing/renaming a key
 * IS a breaking change — old answers will fail validation. Mark as
 * `deprecated: true` instead so existing answers still render.
 */

export interface BioPrompt {
  /** Stable key stored in `profile.prompt_answers[].promptKey`. */
  key: string;
  /** i18n key for the question. */
  questionKey: string;
  /** Optional category tag for the prompt picker grouping. */
  category: 'about_me' | 'preferences' | 'islamic_life' | 'fun' | 'family';
  /** Default placeholder shown in the answer textarea. */
  placeholderKey: string;
  /** True once we want to retire a prompt without breaking saved
   *  answers. Prompt picker hides deprecated entries; profile view
   *  still renders the answer if a user has one. */
  deprecated?: boolean;
}

export const BIO_PROMPTS: readonly BioPrompt[] = [
  // ─── About me ──────────────────────────────────────────────────
  {
    key: 'one_word_friends_describe',
    questionKey: 'prompt.one_word_friends_describe',
    category: 'about_me',
    placeholderKey: 'prompt.one_word_friends_describe.placeholder',
  },
  {
    key: 'morning_routine',
    questionKey: 'prompt.morning_routine',
    category: 'about_me',
    placeholderKey: 'prompt.morning_routine.placeholder',
  },
  {
    key: 'weekend_plans',
    questionKey: 'prompt.weekend_plans',
    category: 'about_me',
    placeholderKey: 'prompt.weekend_plans.placeholder',
  },
  {
    key: 'favorite_subject_in_school',
    questionKey: 'prompt.favorite_subject_in_school',
    category: 'about_me',
    placeholderKey: 'prompt.favorite_subject_in_school.placeholder',
  },
  {
    key: 'most_proud_of',
    questionKey: 'prompt.most_proud_of',
    category: 'about_me',
    placeholderKey: 'prompt.most_proud_of.placeholder',
  },
  {
    key: 'what_im_reading',
    questionKey: 'prompt.what_im_reading',
    category: 'about_me',
    placeholderKey: 'prompt.what_im_reading.placeholder',
  },

  // ─── Preferences / fun ─────────────────────────────────────────
  {
    key: 'favorite_destination',
    questionKey: 'prompt.favorite_destination',
    category: 'fun',
    placeholderKey: 'prompt.favorite_destination.placeholder',
  },
  {
    key: 'dream_travel',
    questionKey: 'prompt.dream_travel',
    category: 'fun',
    placeholderKey: 'prompt.dream_travel.placeholder',
  },
  {
    key: 'favorite_meal',
    questionKey: 'prompt.favorite_meal',
    category: 'fun',
    placeholderKey: 'prompt.favorite_meal.placeholder',
  },
  {
    key: 'feelings_about_cats',
    questionKey: 'prompt.feelings_about_cats',
    category: 'fun',
    placeholderKey: 'prompt.feelings_about_cats.placeholder',
  },
  {
    key: 'guilty_pleasure',
    questionKey: 'prompt.guilty_pleasure',
    category: 'fun',
    placeholderKey: 'prompt.guilty_pleasure.placeholder',
  },
  {
    key: 'comfort_show',
    questionKey: 'prompt.comfort_show',
    category: 'fun',
    placeholderKey: 'prompt.comfort_show.placeholder',
  },

  // ─── Islamic life ──────────────────────────────────────────────
  {
    key: 'what_does_halal_mean_to_me',
    questionKey: 'prompt.what_does_halal_mean_to_me',
    category: 'islamic_life',
    placeholderKey: 'prompt.what_does_halal_mean_to_me.placeholder',
  },
  {
    key: 'how_i_grew_in_faith',
    questionKey: 'prompt.how_i_grew_in_faith',
    category: 'islamic_life',
    placeholderKey: 'prompt.how_i_grew_in_faith.placeholder',
  },
  {
    key: 'ramadan_tradition',
    questionKey: 'prompt.ramadan_tradition',
    category: 'islamic_life',
    placeholderKey: 'prompt.ramadan_tradition.placeholder',
  },
  {
    key: 'favorite_ayah_or_hadith',
    questionKey: 'prompt.favorite_ayah_or_hadith',
    category: 'islamic_life',
    placeholderKey: 'prompt.favorite_ayah_or_hadith.placeholder',
  },
  {
    key: 'role_of_faith_in_marriage',
    questionKey: 'prompt.role_of_faith_in_marriage',
    category: 'islamic_life',
    placeholderKey: 'prompt.role_of_faith_in_marriage.placeholder',
  },

  // ─── Family ────────────────────────────────────────────────────
  {
    key: 'family_in_my_life',
    questionKey: 'prompt.family_in_my_life',
    category: 'family',
    placeholderKey: 'prompt.family_in_my_life.placeholder',
  },
  {
    key: 'parents_in_few_words',
    questionKey: 'prompt.parents_in_few_words',
    category: 'family',
    placeholderKey: 'prompt.parents_in_few_words.placeholder',
  },
  {
    key: 'i_want_my_future_family_to',
    questionKey: 'prompt.i_want_my_future_family_to',
    category: 'family',
    placeholderKey: 'prompt.i_want_my_future_family_to.placeholder',
  },

  // ─── Looking for / preferences ─────────────────────────────────
  {
    key: 'looking_for_in_a_partner',
    questionKey: 'prompt.looking_for_in_a_partner',
    category: 'preferences',
    placeholderKey: 'prompt.looking_for_in_a_partner.placeholder',
  },
  {
    key: 'deal_breaker',
    questionKey: 'prompt.deal_breaker',
    category: 'preferences',
    placeholderKey: 'prompt.deal_breaker.placeholder',
  },
  {
    key: 'how_we_will_first_meet_in_jannah',
    questionKey: 'prompt.how_we_will_first_meet_in_jannah',
    category: 'preferences',
    placeholderKey: 'prompt.how_we_will_first_meet_in_jannah.placeholder',
  },
  {
    key: 'my_love_language',
    questionKey: 'prompt.my_love_language',
    category: 'preferences',
    placeholderKey: 'prompt.my_love_language.placeholder',
  },
  {
    key: 'green_flag',
    questionKey: 'prompt.green_flag',
    category: 'preferences',
    placeholderKey: 'prompt.green_flag.placeholder',
  },

  // ─── Misc fun ──────────────────────────────────────────────────
  {
    key: 'hidden_talent',
    questionKey: 'prompt.hidden_talent',
    category: 'fun',
    placeholderKey: 'prompt.hidden_talent.placeholder',
  },
  {
    key: 'a_perfect_first_date',
    questionKey: 'prompt.a_perfect_first_date',
    category: 'fun',
    placeholderKey: 'prompt.a_perfect_first_date.placeholder',
  },
  {
    key: 'song_stuck_in_my_head',
    questionKey: 'prompt.song_stuck_in_my_head',
    category: 'fun',
    placeholderKey: 'prompt.song_stuck_in_my_head.placeholder',
  },
  {
    key: 'last_great_book',
    questionKey: 'prompt.last_great_book',
    category: 'fun',
    placeholderKey: 'prompt.last_great_book.placeholder',
  },
  {
    key: 'one_thing_to_learn_this_year',
    questionKey: 'prompt.one_thing_to_learn_this_year',
    category: 'about_me',
    placeholderKey: 'prompt.one_thing_to_learn_this_year.placeholder',
  },
] as const;

export const BIO_PROMPT_KEYS: ReadonlySet<string> = new Set(BIO_PROMPTS.map((p) => p.key));

/** Lookup a prompt by key, undefined if not found. */
export function getBioPrompt(key: string): BioPrompt | undefined {
  return BIO_PROMPTS.find((p) => p.key === key);
}

/** Max number of prompt-answers a user can save. */
export const MAX_BIO_PROMPT_ANSWERS = 5;

/** Max length of a single prompt answer. */
export const MAX_BIO_PROMPT_ANSWER_LENGTH = 120;
